import { SITE_URL } from "@/lib/config";
import { ldJson } from "@/lib/safeUrl";

// ── Canonical entity anchors ──────────────────────────────────────────────
// Every leaf schema (Service, Product, Article, breadcrumb publisher, …)
// references these by @id instead of re-declaring the organization inline, so
// engines resolve one authoritative node that all pages reinforce (policy §9.4).
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const ORG_NAME = "LexVio.ai";

// One canonical, verified social list — used identically everywhere (§9.1).
const SAME_AS = [
  "https://x.com/lexvioai",
  "https://linkedin.com/company/lexvioai",
  "https://youtube.com/@lexvioai",
];

// Real PNG logo (generated by src/app/icon.tsx → /icon), not a 404 (§1.3/§3.1).
const ORG_LOGO = `${SITE_URL}/icon`;

type ProductSpec = {
  name: string;
  slug: string;
  description: string;
  category: string;
  features: string[];
};

const PRODUCTS: ProductSpec[] = [
  {
    name: "Legal AI",
    slug: "legal-ai",
    description:
      "AI contract review, redlining and drafting grounded in the contract's governing law, with a verdict on every citation, plus matters, a case studio and a client portal.",
    category: "BusinessApplication",
    features: [
      "Clause-by-Clause Contract Review",
      "AI Redlining",
      "Citation Verdicts",
      "Case Intelligence Studio",
      "Client Portal",
      "Legal Health Score",
      "Fix Suggestions",
    ],
  },
  {
    name: "Compliance AI",
    slug: "compliance-ai",
    description:
      "A privacy programme under your country's data-protection law — fourteen law packs, each marked drafted until counsel checks it. Not legal advice.",
    category: "BusinessApplication",
    features: [
      "Applicability and Obligation Register",
      "Individual Rights Requests",
      "Breach Notification Clocks",
      "Retention and Deletion",
      "Processor and DPA Review",
      "Impact Assessments",
    ],
  },
  {
    name: "Workflow Agents",
    slug: "workflow-agents",
    description:
      "Legal workflows whose due dates are computed from the facts you give, with approvals decided by role.",
    category: "BusinessApplication",
    features: [
      "Workflow Engine",
      "Due Dates from Stated Facts",
      "Role-Gated Approvals",
      "Client Steps through the Portal",
      "Matter and Contract Workflows",
    ],
  },
];

function ldScript(data: unknown) {
  return (
    <script
      dangerouslySetInnerHTML={{ __html: ldJson(data) }}
      type="application/ld+json"
    />
  );
}

// ── Organization (singleton, #organization) ────────────────────────────────
export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: ORG_NAME,
    alternateName: "LexVio",
    legalName: "Global Synapse Technologies",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: ORG_LOGO,
      width: 512,
      height: 512,
    },
    image: ORG_LOGO,
    description: "The Global AI Legal Operating System — contracts, matters and privacy, with the source behind every answer, across jurisdictions.",
    foundingDate: "2026",
    knowsAbout: [
      "Contract review",
      "Legal artificial intelligence",
      "Citation verification",
      "Data protection and privacy compliance",
      "Contract redlining",
      "Regulatory compliance automation",
      "Legal knowledge graphs",
      "AI legal agents",
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Delhi",
      addressRegion: "Delhi",
      addressCountry: "IN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+91-92057-57755",
      email: "info@globalsynapsetech.com",
      contactType: "customer service",
      availableLanguage: ["English"],
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "LexVio.ai products",
      itemListElement: PRODUCTS.map((p) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: p.name,
          description: p.description,
          url: `${SITE_URL}/product/${p.slug}`,
        },
      })),
    },
    sameAs: SAME_AS,
  };
  return ldScript(schema);
}

// ── WebSite (singleton, #website) ──────────────────────────────────────────
export function WebSiteSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: ORG_NAME,
    url: SITE_URL,
    inLanguage: "en",
    publisher: { "@id": ORG_ID },
    // No SearchAction: it pointed at `${APP_URL}/search?q={search_term_string}`,
    // an endpoint nobody has verified exists (audit: "verify or drop"). Restore
    // the potentialAction once app.lexvio.ai/search is confirmed to accept ?q=.
  };
  return ldScript(schema);
}

// Convenience: emit both singletons together (used site-wide via SiteEntityGraph).
export function SiteEntityGraph() {
  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
    </>
  );
}

export function SoftwareSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: ORG_NAME,
    url: SITE_URL,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    // No free plan exists: the lowest global plan is Starter, $6 a month
    // (g-backend subscription_plans.py global_starter). Free is the scanner.
    offers: {
      "@type": "Offer",
      price: "6",
      priceCurrency: "USD",
      name: "Starter",
    },
    description:
      "AI-powered contract review, redlining, matter and case analysis, a client portal and a privacy programme, with a verdict on every citation.",
    featureList:
      "Contract Review, AI Redlining, Case Intelligence Studio, Client Portal, Privacy Programme, Legal Health Score, Workflow Agents",
    publisher: { "@id": ORG_ID },
  };
  return ldScript(schema);
}

// ── FAQPage (generic, reusable) ────────────────────────────────────────────
// IMPORTANT: only emit this where the SAME Q&As are visible on the page
// (Google FAQ guidelines). Pair it with a visible FAQ section (see FaqSection).
export function FaqJsonLd({ faqs }: { faqs: { q: string; a: string }[] }) {
  if (!faqs?.length) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return ldScript(schema);
}

export function BreadcrumbSchema({
  items,
}: {
  items: { name: string; url: string }[];
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
  return ldScript(schema);
}

// ── Service (per solution/offering) ────────────────────────────────────────
export function ServiceSchema({
  name,
  description,
  url,
  serviceType,
}: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    ...(serviceType ? { serviceType } : {}),
    url: `${SITE_URL}${url}`,
    provider: { "@id": ORG_ID },
  };
  return ldScript(schema);
}

export function ProductSchema({ slug }: { slug: string }) {
  const p = PRODUCTS.find((x) => x.slug === slug);
  if (!p) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: `${p.name} — LexVio.ai`,
    applicationCategory: p.category,
    operatingSystem: "Web",
    url: `${SITE_URL}/product/${p.slug}`,
    description: p.description,
    featureList: p.features.join(", "),
    // No free plan exists: the lowest global plan is Starter, $6 a month
    // (g-backend subscription_plans.py global_starter). Free is the scanner.
    offers: {
      "@type": "Offer",
      price: "6",
      priceCurrency: "USD",
      name: "Starter",
    },
    // No aggregateRating: ship rating markup only with real, on-page reviews.
    provider: { "@id": ORG_ID },
    brand: { "@type": "Brand", name: ORG_NAME },
  };
  return ldScript(schema);
}

// ── Freshness: WebPage + dateModified ──────────────────────────────────────
// Many pages state "Last reviewed: <date>" in prose while emitting no
// machine-readable equivalent. For statutory content, currency IS the value
// proposition, so the reviewed date has to reach the engines that decide
// whether a claim is still good. `reviewedOn` must be an ISO date (YYYY-MM-DD)
// and must be the SAME value the page renders — never a build timestamp, which
// would assert a review that nobody performed.
// dateModified must be ISO-8601. Several data files keep a prose twin
// ("19 August 2026") beside the ISO one; passing the prose form would emit
// invalid structured data, so it is dropped rather than published.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function ReviewedPageSchema({
  path,
  name,
  description,
  reviewedOn,
  speakableSelectors,
}: {
  path: string;
  name: string;
  description?: string;
  reviewedOn: string;
  speakableSelectors?: string[];
}) {
  const url = `${SITE_URL}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name,
    ...(description ? { description } : {}),
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    inLanguage: "en",
    ...(ISO_DATE.test(reviewedOn) ? { dateModified: reviewedOn } : {}),
    ...(speakableSelectors?.length
      ? {
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: speakableSelectors,
          },
        }
      : {}),
  };
  return ldScript(schema);
}

// ── Hub pages: CollectionPage + ItemList ───────────────────────────────────
// Index pages enumerate something (tools, features, practice areas, terms).
// Without a list node an answer engine has to infer the inventory from links;
// with one it can enumerate it exactly. Keep `items` in the same order the page
// renders them — position is meaningful.
export function ItemListSchema({
  path,
  name,
  description,
  items,
  reviewedOn,
}: {
  path: string;
  name: string;
  description?: string;
  items: { name: string; url: string; description?: string }[];
  reviewedOn?: string;
}) {
  if (!items?.length) return null;
  const url = `${SITE_URL}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    url,
    name,
    ...(description ? { description } : {}),
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    inLanguage: "en",
    ...(reviewedOn && ISO_DATE.test(reviewedOn) ? { dateModified: reviewedOn } : {}),
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        ...(item.description ? { description: item.description } : {}),
        url: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
      })),
    },
  };
  return ldScript(schema);
}

// ── Dataset (the statutory corpora) ────────────────────────────────────────
// The deadline calendar, TDS section table, stamp-duty schedule and limitation
// entries are genuine curated datasets with a known size and review date.
// Dataset is the honest type for them.
//
// Deliberately NOT schema.org/Event for filing deadlines: an Event is something
// that occurs and that people attend, and Google's guidelines are explicit that
// non-events must not be marked up as events. A GST return due date is an
// obligation, not an occurrence — Event markup would also require a location we
// would have to invent. Dataset + ItemList carries the same information to
// answer engines without asserting a type that is not true.
export function DatasetSchema({
  path,
  name,
  description,
  reviewedOn,
  keywords,
  temporalCoverage,
  numberOfItems,
  spatialCoverage,
}: {
  path: string;
  name: string;
  description: string;
  reviewedOn?: string;
  keywords?: string[];
  temporalCoverage?: string;
  numberOfItems?: number;
  /** Country the dataset covers, or null for none. Required and explicit:
   *  this used to be hardcoded to "India", which a future global dataset
   *  would silently inherit (audit 2026-08-31 §B5). Every current caller is
   *  an India-only page and passes "India". */
  spatialCoverage: string | null;
}) {
  const url = `${SITE_URL}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "@id": `${url}#dataset`,
    name,
    description,
    url,
    isAccessibleForFree: true,
    creator: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    inLanguage: "en",
    ...(spatialCoverage
      ? { spatialCoverage: { "@type": "Country", name: spatialCoverage } }
      : {}),
    ...(reviewedOn && ISO_DATE.test(reviewedOn) ? { dateModified: reviewedOn } : {}),
    ...(keywords?.length ? { keywords: keywords.join(", ") } : {}),
    ...(temporalCoverage ? { temporalCoverage } : {}),
    ...(numberOfItems ? { size: `${numberOfItems} entries` } : {}),
  };
  return ldScript(schema);
}
